import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { NUMBER_FIELD_WARNING, OpCode, Select, shouldShowNumberFieldWarning } from '@aline/common';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { AlineInput } from 'src/AlineInput';
import type { PanelVariableType } from 'src/components';
import {
  AlineTooltip,
  useOnChangeFocusVariable,
  useUpdatePrimitiveVariableValue,
  useVariableStyles,
  VariableRowHeader,
} from 'src/components';
import { NumberIcon } from 'src/components/icons/NumberIcon';
import { useGetFieldDisabledReason } from 'src/editor/popovers';
import { domElIdCreators } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  endAdornment: {
    marginRight: theme.spacing(1),
  },
}));

interface NumberVariableProps extends React.HTMLAttributes<HTMLElement> {
  documentId: UUID;
  isSelected: boolean;
  numVariable: PanelVariableType;
}

export function NumberVariable({
  className,
  documentId,
  isSelected,
  numVariable,
}: NumberVariableProps): JSX.Element {
  const { classes: numberVarClasses } = useStyles();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [liEl, setLiEl] = useState<HTMLLIElement | null>(null);
  const { disabledReason } = useGetFieldDisabledReason(numVariable);
  const [value, setValue] = useState<string>(numVariable.value ?? '');
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
  const { onClickVariable, resetOnBlur, updatePrimitiveVariable } = useUpdatePrimitiveVariableValue(
    numVariable,
    {
      isFocused,
      syncVariableValue: v => setValue(v.value ?? ''),
    }
  );
  const isCompleted = typeof numVariable.value === 'string' || Boolean(numVariable.selectedAnswer);
  const { classes } = useVariableStyles({ isCompleted, isSelected });

  const { onFocus } = useOnChangeFocusVariable(numVariable, { inputEl, liEl });

  return (
    <li
      ref={setLiEl}
      aria-selected={isSelected}
      className={clsx(className, classes.variable)}
      data-cy={Select.document.editor.rightDrawer.variable.list.row.container}
      id={domElIdCreators.variableRowEl(numVariable.nodeId)}
      onClick={onClickVariable}
      onFocus={onFocus}
    >
      <VariableRowHeader documentId={documentId} isCompleted={isCompleted} variable={numVariable} />
      {numVariable.question && (
        <Typography className={classes.question}>{numVariable.question}</Typography>
      )}
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <AlineInput
            dataCy={Select.document.editor.rightDrawer.outline.numberFieldRowInput}
            disabled={Boolean(disabledReason)}
            endAdornment={<NumberIcon className={numberVarClasses.endAdornment} />}
            helperText={NUMBER_FIELD_WARNING}
            hideHelperText={!shouldShowNumberFieldWarning(value)}
            inputClassName={classes.input}
            inputRef={setInputEl}
            placeholder="Enter number..."
            value={value}
            onBlur={() => {
              const opCode = resetOnBlur();
              if (opCode === OpCode.Success) setValue('');
              setIsFocused(false);
            }}
            onChange={async e => {
              const nextValue = e.target.value;
              setValue(nextValue);
              await updatePrimitiveVariable({
                formattedValue: nextValue,
                value: nextValue,
              });
            }}
            onFocus={e => {
              e.target.select();
              setIsFocused(true);
            }}
          />
        </div>
      </AlineTooltip>
    </li>
  );
}
